/**
 * TypeScript mirrors of the Portal API contracts that MDenture consumes.
 *
 * These intentionally mirror the shapes produced by the existing Portal server
 * (Server/Portal) so no second/competing contract is introduced:
 *  - Utils/Portal/Models/EResult.cs
 *  - Utils/Portal/Models/PortalResult.cs
 *  - Utils/Portal/Models/UserToken.cs
 *  - Utils/Portal/Models/User.cs
 *
 * The Portal serialises responses in camelCase (CamelCasePropertyNamesContractResolver)
 * and flattens managed properties (IPropertyJSON) to their raw values.
 */

/** Subset of Dental.Utils.Portal.EResult actually handled by the client. */
export enum EResult {
  Success = 0,
  NoLicense = 1,
  IncorrectVersion = 3,
  NoServer = 4,
  InvalidPassword = 5,
  InvalidLogin = 6,
  NotUnique = 7,
  AccessDenied = 8,
  NotFound = 9,
  NotValid = 14,
  AlreadyLoggedIn = 26,
  UserLocked = 27,
  UserNotActivated = 28,
  ClientError = 126,
  ServerError = 127,
  InvalidToken = 134,
}

/** Mirror of Dental.Utils.Portal.PortalResult<T>. */
export interface PortalResult<T = undefined> {
  serverResult: EResult;
  message?: string | null;
  data?: T;
  timeStampUTC?: string;
}

/** Result codes returned by the SuiteAPI endpoints consumed by MDenture. */
export enum ESuiteApiResult {
  Success = 0,
  AccessDenied = 5,
  NotFound = 6,
  NoSpace = 7,
  NoLicense = 0x0001_0001,
  IncorrectVersion = 0x4000_0003,
  NoConnection = 0x4000_0002,
  InvalidLogin = 0x0002_0001,
  InvalidPassword = 0x0002_0002,
  UserLocked = 0x0002_0003,
  InvalidToken = 0x0002_0004,
  NotUnique = 0x0002_0005,
  UserNotActivated = 0x0002_0006,
  AlreadyLoggedIn = 0x0002_0007,
  BadRequest = 0x4000_0004,
  FilePending = 0x1600_0001,
  NotValid = 0x1600_0002,
  ServerError = 0x4000_ff02,
  /**
   * Returned by `PUT SuiteAPI/Order/OrderNoteAdd` when the other side wrote new notes since the
   * caller last synced: the submitted note was NOT added, `data` carries the missed notes instead.
   * Not an error — the caller should merge the notes in and let the user resend.
   */
  NewNotesAvailable = 0x4000_0005,
}

/** Body of Dental.Utils.Portal.UserToken as serialised for the API. */
export interface UserTokenPayload {
  userLogin: string;
  userPassword: string;
  machineKey: string;
}

/** Body of Dental.Utils.Portal.PortalRequest. */
export interface PortalRequest {
  userToken: UserTokenPayload;
}

/**
 * Subset of Dental.Utils.Portal.User that the UI uses. The Portal returns many
 * more fields; unknown fields are simply ignored.
 */
export interface PortalUser {
  id?: string;
  profileID?: string;
  eMail?: string;
  title?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  initials?: string | null;
  orgID?: string | null;
  orgName?: string | null;
  orgDisplayName?: string | null;
  language?: string | null;
}

/** Result of the authenticated `SuiteAPI/User/GetProfile` endpoint. */
export interface UserProfileSettings {
  firstName: string;
  lastName: string;
  email: string;
  notifyByMail: boolean;
}

/** Body of the authenticated `SuiteAPI/User/UpdateProfile` endpoint. */
export interface UserProfileUpdateRequest {
  firstName: string;
  lastName: string;
  notifyByMail: boolean;
}

/** Credentials required to (re)authenticate against the Portal on every request. */
export interface Credentials {
  email: string;
  password: string;
}

/**
 * Body of the `PUT SuiteAPI/User/SelfRegisterV1` contract. Mirrors the fields of
 * Server/Portal/Areas/SuiteAPI/RequestModels/SelfRegisterRequest.cs.
 */
export interface RegisterAccountRequest {
  title?: string;
  firstName: string;
  lastName: string;
  email: string;
  newPassword: string;
}

/** Bitmask mirroring Dental.Portal.DB.EOrganizationType. */
export enum EOrganizationType {
  Practice = 1,
  Lab = 2,
  Praxislabor = Practice | Lab,
}

/**
 * Body of the `PUT SuiteAPI/Organization/AddV1` contract (registration step 2 — company info).
 * Mirrors Server/Portal/Areas/SuiteAPI/RequestModels/OrganizationAddRequest.cs.
 */
export interface OrganizationAddRequest {
  displayName: string;
  /** Bitmask of {@link EOrganizationType}; at least one flag must be set. */
  organizationType: number;
  createSpectraXUnassignedCase: boolean;
  /** Optional address created in the same transaction. */
  address?: AddressRequest;
  /** MDenture/Merz-specific: customer number used as the organization NickName when provided. */
  merzCustomerNumber?: string;
}

/** Result of the authenticated `PUT SuiteAPI/Organization/AddV1` endpoint. */
export interface OrganizationAddResult {
  id: string;
  nickName?: string | null;
  displayName?: string | null;
}

/**
 * Body of the `PUT SuiteAPI/Organization/AddressAddV1` contract (and the optional nested address
 * on `.../AddV1`). Mirrors Server/Portal/Areas/SuiteAPI/RequestModels/AddressRequest.cs.
 */
export interface AddressRequest {
  id?: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  countryID: string;
  phone?: string;
  email?: string;
  vat?: string;
  isBillingAddress: boolean;
  isShippingAddress: boolean;
}

/** Organization address row returned by SuiteAPI/Organization/AddressesV1. */
export interface OrganizationAddress {
  id: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  countryID: string;
  country: string;
  phone?: string | null;
  eMail?: string | null;
  vat?: string | null;
  isBillingAddress: boolean;
  isShippingAddress: boolean;
}

/**
 * Mirror of Dental.Portal.Areas.SuiteAPI.ResultModels.AddressResult.
 * Backs the organization address list in the Settings page.
 */
export interface AddressDto {
  id: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  countryID: string;
  country: string;
  phone: string;
  email: string;
  vat: string;
  isBillingAddress: boolean;
  isShippingAddress: boolean;
}

/**
 * Mirror of Dental.Portal.Areas.SuiteAPI.ResultModels.CountryResult.
 * Backs country pickers in the registration wizard.
 */
export interface CountryOption {
  id: string;
  iso2: string;
  /** Translated display name of the country. */
  name: string;
}

/**
 * Mirror of Dental.Portal.Areas.SuiteAPI.ResultModels.OrganizationSettingsResult. Backs the
 * "Organization" section of the Settings page (`SuiteAPI/Organization/GetV1` and `.../UpdateV1`).
 */
export interface OrganizationSettings {
  displayName: string;
  /** Bitmask of {@link EOrganizationType}. */
  organizationType: number;
}

/**
 * Body of the `PUT SuiteAPI/Organization/UpdateV1` contract (Settings page — organization section).
 * Mirrors Server/Portal/Areas/SuiteAPI/RequestModels/OrganizationUpdateRequest.cs.
 */
export interface OrganizationUpdateRequest {
  displayName: string;
  /** Bitmask of {@link EOrganizationType}; at least one flag must be set. */
  organizationType: number;
}

/**
 * Body of the anonymous `PUT SuiteAPI/User/ConfirmEmailV1` contract.
 * Mirrors Server/Portal/Areas/SuiteAPI/RequestModels/ConfirmEmailRequest.cs.
 */
export interface ConfirmEmailRequest {
  /** User-ID from the activation link's `id` query parameter. */
  userID: string;
  /** Activation token from the activation link's `token` query parameter. */
  token: string;
}

/**
 * Result of `PUT SuiteAPI/User/ConfirmEmailV1`. Carries the JWT bearer token plus
 * minimal identity data needed to continue the registration wizard.
 * Mirrors Server/Portal/Areas/SuiteAPI/ResultModels/ConfirmEmailResult.cs.
 */
export interface ConfirmEmailResult {
  jwt: string;
  userID: string;
  email: string;
  orgID: string | null;
  brandID: string;
}

/** Compact order list item returned by SuiteAPI/Order/GetProjectOrders. */
export interface ProjectOrderListItem {
  id: string;
  orderNumber: string;
  created: string;
  projectName: string;
  orderState: string;
  orderStateId: string;
  orderStepId: string;
  unreadMessageCount: number;
  internalCaseId?: string;
}

/** Compact order details item returned by SuiteAPI/Order/GetProjectOrder. */
export interface ProjectOrderDetails {
  id: string;
  orderNumber: string;
  created: string;
  projectName: string;
  orderState: string;
  customerReference: string;
  notes: string;
  deliveryMethod: string;
  deliveryDateUtc?: string | null;
  orderType: string;
  toothColor?: string;
  internalCaseId?: string;
  serviceName?: string;
  servicePrice?: number | null;
  serviceVat?: number | null;
  serviceCurrency?: string;
  finishingWishes?: string[];
  hasQualityProtocol: boolean;
  scanFiles?: ProjectOrderScanFile[];
  flowSteps?: ProjectOrderFlowStep[];
  canCancel: boolean;
  canUploadFiles: boolean;
}

/** Body of `PUT SuiteAPI/Order/ReviewDesignV1`. */
export interface ReviewDesignRequest {
  orderID: string;
  isAccepted: boolean;
  rejectionReason?: string;
}

export interface ProjectOrderScanFile {
  id?: string | null;
  name: string;
  type: string;
  sizeInKB?: number | null;
  uploadedAtUtc: string;
  isAdditionalFile: boolean;
  isProtocolFile: boolean;
  isScanFile?: boolean;
}

export interface ProjectOrderFlowStep {
  key: string;
  displayName: string;
  sequenceNumber: number;
}

/** Flow state returned by `SuiteAPI/Order/FlowStatesV1`. */
export interface OrderFlowState {
  /** Numeric enum values are used by the Portal unless a string converter is configured. */
  orderType: string | number;
  id: string;
  orderStepId: string;
  name: string;
  displayName: string;
  sequenceNumber: number;
  isActive: boolean;
  isEnabled: boolean;
  groupID?: string | null;
}

/** File metadata returned after reserving an additional order upload. */
export interface AdditionalOrderFile {
  id: string;
  fileName: string;
  uploadRequired?: boolean;
  category?: 'OtherData' | 'ProtocolData' | null;
  conflictResolvedFileName?: string | null;
  contentType?: string | null;
}

/** Article price row returned by SuiteAPI/Order/Articles. */
export interface OrderArticlePrice {
  productID: string;
  category: string;
  description: string;
  price?: number | null;
  currency?: string | null;
  isAddOnArticle: boolean;
  handlingTime: number;
  pictureUrl?: string | null;
  vat?: number | null;
}

/** Request body for SuiteAPI/Order/CreateOrder. */
export interface CreateOrderRequest {
  productionCenterId: string;
  companyId: string;
  reference: string;
  notes?: string | null;
  toothColor?: string | null;
  articleProductIds: string[];
  /** Selected Mill design files; the first CadData role file is bound to the production CAD article. */
  designFileNames?: string[];
  addressId: string;
  /** 0 = Standard, 1 = Express, 2 = Pickup */
  deliveryMethod: number;
  internalCaseId?: string | null;
}

/** Available delivery methods returned by SuiteAPI/Order/DeliveryMethods. */
export interface DeliveryMethodsResult {
  standardPrice?: number | null;
  standardDays?: number | null;
  expressPrice?: number | null;
  expressDays?: number | null;
  deliveryCutoffTime?: string | null;
  hasPickup: boolean;
  currency: string;
}

/**
 * Mirror of Dental.Portal.Areas.SuiteAPI.ResultModels.Order.OrderNoteItem.
 * Backs the order case-chat (`GetOrderNotes`, `OrderNoteAdd`, `OrderNotesMarkAsRead`) —
 * these SuiteAPI endpoints reuse the existing Portal/Manager case-chat logic unchanged.
 */
export interface OrderNoteItem {
  id: string;
  content: string;
  createdBy: string;
  /** Author name plus their organization/group name in brackets, e.g. "Ada Lovelace (Acme Dental)". */
  createdByText: string;
  createdUtc: string;
  readUtc: string | null;
  /** True when written by the customer (this app); false when written by the lab. */
  isCustomerNote: boolean;
}

/** Body of the `PUT SuiteAPI/Order/OrderNoteAdd` contract. */
export interface OrderNoteAddRequest {
  orderID: string;
  content: string;
  /** IDs of notes the caller has locally but not yet marked as read, sent along for bookkeeping. */
  unreadNoteIds: string[];
  isCustomer: boolean;
  sendEmailNotification: boolean;
}

/** Body of the `PUT SuiteAPI/Order/OrderNotesMarkAsRead` contract. */
export interface OrderNotesMarkAsReadRequest {
  orderID: string;
  unreadNoteIds: string[];
}
